import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface ExportProjectModalProps {
  count: number;
  onConfirm: (dontShowAgain: boolean) => void;
  onCancel: () => void;
}

export function ExportProjectModal({ count, onConfirm, onCancel }: ExportProjectModalProps) {
  const { colors } = useTheme();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <View style={[styles.header, { backgroundColor: colors.blue.background, borderColor: colors.blue.border }]}>
        <MaterialCommunityIcons name="export-variant" size={24} color={colors.blue.text} />
        <ThemedText type="subtitle" style={{ color: colors.blue.text, marginLeft: 8 }}>
          Export to Sage
        </ThemedText>
      </View>

      <View style={styles.body}>
        <ThemedText style={styles.message}>
          You&apos;re about to export <ThemedText style={styles.count}>{count}</ThemedText> project
          {count !== 1 ? "s" : ""} to Sage.
        </ThemedText>

        <Pressable style={styles.checkboxRow} onPress={() => setDontShowAgain(!dontShowAgain)}>
          <MaterialCommunityIcons
            name={dontShowAgain ? "checkbox-marked" : "checkbox-blank-outline"}
            size={22}
            color={dontShowAgain ? colors.primary : colors.textSecondary}
          />
          <ThemedText style={[styles.checkboxLabel, { color: colors.textSecondary }]}>
            Don&apos;t show this message again
          </ThemedText>
        </Pressable>
      </View>

      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Export"
          icon="export-variant"
          onPress={() => onConfirm(dontShowAgain)}
          style={[styles.exportButton, { backgroundColor: colors.blue.background, borderColor: colors.blue.border }]}
          textStyle={{ color: colors.blue.text }}
          iconStyle={{ color: colors.blue.text }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: 420, borderRadius: 12, borderWidth: 1, overflow: "hidden" },
  header: { flexDirection: "row", alignItems: "center", padding: 16, borderBottomWidth: 1 },
  body: { padding: 16, gap: 16 },
  message: { fontSize: 15, lineHeight: 22 },
  count: { fontWeight: "700", fontSize: 16 },
  infoBox: { flexDirection: "row", alignItems: "center", padding: 12, borderRadius: 8, borderWidth: 1, gap: 10 },
  infoText: { fontSize: 14, fontWeight: "500", flex: 1 },
  checkboxRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  checkboxLabel: { fontSize: 13 },
  footer: { flexDirection: "row", justifyContent: "flex-end", gap: 8, padding: 12 },
  cancelButton: { height: 36, paddingHorizontal: 16, backgroundColor: "transparent", borderWidth: 1 },
  exportButton: { height: 36, paddingHorizontal: 16, borderWidth: 1 },
});
